import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useOptimizedCalculations } from '@/hooks/useOptimizedCalculations';
import { useEventThrottling } from '@/hooks/useEventThrottling';

export type Gender = 'male' | 'female';
export type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
export type Goal = 'lose' | 'maintain' | 'gain';

const formSchema = z.object({
  gender: z.enum(['male', 'female']),
  age: z.coerce.number().min(10).max(100),
  weight: z.coerce.number().min(30).max(200),
  height: z.coerce.number().min(100).max(250),
  activityLevel: z.enum(['sedentary', 'light', 'moderate', 'active', 'very_active']),
  goal: z.enum(['lose', 'maintain', 'gain']),
  mealsPerDay: z.coerce.number().min(1).max(6)
});

export type FormValues = z.infer<typeof formSchema>;

export type CalculationResults = FormValues & {
  targetCalories: number;
  targetProtein: number;
  targetCarbs: number;
  targetFat: number;
  targetIron?: number;
  targetCalcium?: number;
  targetZinc?: number;
  targetMagnesium?: number;
  targetOmega3?: number;
};

interface CaloriesCalculatorProps {
  initialValues?: Partial<FormValues>;
  onSave: (data: CalculationResults) => void;
}

export const CaloriesCalculator: React.FC<CaloriesCalculatorProps> = ({ 
  initialValues, 
  onSave 
}) => {
  const [results, setResults] = useState<CalculationResults | null>(null);
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  
  const { nutritionTargets } = useOptimizedCalculations(user?.profile);
  const { throttledEventHandler } = useEventThrottling();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      gender: initialValues?.gender || 'male',
      age: initialValues?.age || 30,
      weight: initialValues?.weight || 70,
      height: initialValues?.height || 170,
      activityLevel: initialValues?.activityLevel || 'moderate',
      goal: initialValues?.goal || 'maintain',
      mealsPerDay: initialValues?.mealsPerDay || 3
    }
  });

  useEffect(() => {
    if (initialValues) {
      form.reset({
        gender: initialValues.gender || 'male',
        age: initialValues.age || 30,
        weight: initialValues.weight || 70,
        height: initialValues.height || 170,
        activityLevel: initialValues.activityLevel || 'moderate',
        goal: initialValues.goal || 'maintain',
        mealsPerDay: initialValues.mealsPerDay || 3
      });
    }
  }, [initialValues, form]);

  const calculateBMR = (data: FormValues): number => {
    if (data.gender === 'male') {
      return 10 * data.weight + 6.25 * data.height - 5 * data.age + 5;
    } else {
      return 10 * data.weight + 6.25 * data.height - 5 * data.age - 161;
    }
  };

  const calculateTDEE = (bmr: number, activityLevel: ActivityLevel): number => {
    const activityMultipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      very_active: 1.9
    };
    
    return bmr * activityMultipliers[activityLevel];
  };

  const calculateTargetCalories = (tdee: number, goal: Goal): number => {
    switch (goal) {
      case 'lose': return Math.round(tdee * 0.8);
      case 'gain': return Math.round(tdee * 1.1);
      default: return Math.round(tdee);
    }
  };

  const calculateNutrition = (data: FormValues): CalculationResults => {
    const bmr = calculateBMR(data);
    const tdee = calculateTDEE(bmr, data.activityLevel);
    const calculatedCalories = calculateTargetCalories(tdee, data.goal);
    
    // ENFORCE MINIMUM 1500 CALORIES - NO EXCEPTIONS
    const targetCalories = Math.max(1500, calculatedCalories);
    
    // Log if we had to adjust calories
    if (calculatedCalories < 1500) {
      console.log(`🔒 MINIMUM CALORIES ENFORCED: Original calculation was ${calculatedCalories} kcal, adjusted to 1500 kcal`);
    }
    
    // ALWAYS use 0.8 g/kg for protein calculation regardless of goal
    const targetProtein = Math.round(data.weight * 0.8);
    
    console.log('🧮 PROTEIN CALCULATION DEBUG:', {
      weight: data.weight,
      calculation: `${data.weight} * 0.8 = ${data.weight * 0.8}`,
      rounded: targetProtein,
      goal: data.goal
    });
    
    const targetFat = Math.round((targetCalories * 0.25) / 9);
    
    const targetCarbs = Math.round((targetCalories - (targetProtein * 4) - (targetFat * 9)) / 4);
    
    const isMale = data.gender === 'male';
    const targetIron = isMale ? 8 : 18;
    const targetCalcium = 1000;
    const targetZinc = 11;
    const targetMagnesium = 400;
    const targetOmega3 = 1.6;
    
    return {
      ...data,
      targetCalories,
      targetProtein,
      targetCarbs,
      targetFat,
      targetIron,
      targetCalcium,
      targetZinc,
      targetMagnesium,
      targetOmega3
    };
  };

  const onSubmit = (data: FormValues) => {
    try {
      const calculatedResults = calculateNutrition(data);
      console.log('📊 CALCULATION RESULTS:', calculatedResults);
      setResults(calculatedResults);
      setIsSaved(false);
    } catch (error) {
      console.error('Error calculating nutrition:', error);
      toast.error('Error al calcular la nutrición');
    }
  };

  const saveToDatabase = async (data: CalculationResults) => {
    if (!user?.id || user.id.startsWith('user-')) {
      return null;
    }
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          gender: data.gender,
          age: data.age,
          weight: data.weight,
          height: data.height,
          activity_level: data.activityLevel,
          goal: data.goal,
          meals_per_day: data.mealsPerDay,
          target_calories: data.targetCalories,
          target_protein: data.targetProtein,
          target_carbs: data.targetCarbs,
          target_fat: data.targetFat,
          target_iron: data.targetIron,
          target_calcium: data.targetCalcium,
          target_zinc: data.targetZinc,
          target_magnesium: data.targetMagnesium,
          target_omega3: data.targetOmega3
        })
        .eq('id', user.id);
        
      if (error) {
        console.error('Error updating profile in database:', error);
        throw error;
      }
      
      return true;
    } catch (error) {
      console.error('Error in saveToDatabase:', error);
      throw error;
    }
  };

  const handleSave = async () => {
    if (!results) {
      toast.error('Por favor, calcula tus valores antes de guardar');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // 🔥 FORZAR LIMPIEZA COMPLETA DEL CACHÉ
      console.log('🧹 Limpiando caché antes de actualizar perfil...');
      
      // Eliminar todos los cachés relacionados con el perfil del usuario
      const cacheKeys = [
        'user_profile_cache',
        `profile_${user?.id}`,
        `nutrition_${user?.id}`,
        'menu_cache',
        'user_data_cache'
      ];
      
      cacheKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`✅ Caché eliminado: ${key}`);
        } catch (error) {
          console.warn(`⚠️ No se pudo eliminar caché: ${key}`, error);
        }
      });

      // Limpiar también el sessionStorage por si acaso
      try {
        sessionStorage.removeItem('user_profile_cache');
        sessionStorage.removeItem(`profile_${user?.id}`);
        console.log('✅ SessionStorage limpiado');
      } catch (error) {
        console.warn('⚠️ No se pudo limpiar sessionStorage', error);
      }

      // Guardar en la base de datos
      await saveToDatabase(results);
      onSave(results);
      
      // 🔄 FORZAR ACTUALIZACIÓN DE TODOS LOS CONTEXTOS
      console.log('🔄 Forzando actualización de contextos...');
      
      // Dispatch events para forzar actualización inmediata
      const nutritionUpdateEvent = new CustomEvent('NUTRITION_PROFILE_UPDATED', {
        detail: {
          nutritionGoals: {
            calories: results.targetCalories,
            protein: results.targetProtein,
            carbs: results.targetCarbs,
            fat: results.targetFat,
            iron: results.targetIron,
            calcium: results.targetCalcium,
            zinc: results.targetZinc,
            magnesium: results.targetMagnesium,
            omega3: results.targetOmega3
          },
          userId: user?.id,
          forceUpdate: true // Nueva bandera para forzar actualización
        }
      });
      
      const mealsUpdateEvent = new CustomEvent('meals_per_day_updated', {
        detail: {
          mealsPerDay: results.mealsPerDay,
          userId: user?.id,
          forceUpdate: true
        }
      });
      
      // Dispatch inmediato
      window.dispatchEvent(nutritionUpdateEvent);
      window.dispatchEvent(mealsUpdateEvent);
      
      // Dispatch adicional con delay para asegurar que todos los componentes se actualicen
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('FORCE_MENU_UPDATE', {
          detail: { timestamp: Date.now() }
        }));
        
        // Event adicional para cualquier listener que pueda haberse perdido
        window.dispatchEvent(new CustomEvent('USER_PROFILE_UPDATED', {
          detail: {
            profile: results,
            userId: user?.id,
            timestamp: Date.now()
          }
        }));
        
        console.log('🎯 Eventos de actualización disparados');
      }, 100);
      
      // Segundo dispatch con más delay para usuarios problemáticos
      setTimeout(() => {
        window.dispatchEvent(nutritionUpdateEvent);
        window.dispatchEvent(mealsUpdateEvent);
        console.log('🔄 Segundo round de eventos disparado');
      }, 500);
      
      toast.success('Perfil nutricional actualizado correctamente');
      setIsSaved(true);
      
      setTimeout(() => {
        setIsSaved(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error saving nutrition profile:', error);
      toast.error('Error al guardar el perfil nutricional');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Género</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona un género" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="male">Masculino</SelectItem>
                      <SelectItem value="female">Femenino</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Usado para calcular tu metabolismo basal.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Edad</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="weight"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Peso (kg)</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="height"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Altura (cm)</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="activityLevel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nivel de Actividad</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tu nivel de actividad" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="sedentary">Sedentario (poco o ningún ejercicio)</SelectItem>
                      <SelectItem value="light">Ligero (ejercicio 1-3 días/semana)</SelectItem>
                      <SelectItem value="moderate">Moderado (ejercicio 3-5 días/semana)</SelectItem>
                      <SelectItem value="active">Activo (ejercicio 6-7 días/semana)</SelectItem>
                      <SelectItem value="very_active">Muy Activo (ejercicio intenso diario)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="goal"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Objetivo</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tu objetivo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="lose">Perder Peso</SelectItem>
                      <SelectItem value="maintain">Mantener Peso</SelectItem>
                      <SelectItem value="gain">Ganar Peso</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="mealsPerDay"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Comidas por día</FormLabel>
                  <Select onValueChange={(value) => field.onChange(parseInt(value))} defaultValue={field.value.toString()}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Número de comidas" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="3">3 comidas</SelectItem>
                      <SelectItem value="4">4 comidas</SelectItem>
                      <SelectItem value="5">5 comidas</SelectItem>
                      <SelectItem value="6">6 comidas</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit">Calcular</Button>
          </div>
        </form>
      </Form>

      {results && (
        <div className="mt-8 p-6 border rounded-lg bg-card">
          <h3 className="text-xl font-semibold mb-4">Resultados del Cálculo</h3>
          
          {results.targetCalories === 1500 && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>Nota:</strong> Tus calorías han sido ajustadas a 1500 kcal (mínimo recomendado para una nutrición saludable).
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <span className="font-medium text-muted-foreground">Calorías Diarias:</span>
                <p className="text-xl font-bold">{results.targetCalories} kcal</p>
              </div>
              
              <div>
                <span className="font-medium text-muted-foreground">Proteínas:</span>
                <p className="text-lg font-bold">{results.targetProtein} g</p>
              </div>
              
              <div>
                <span className="font-medium text-muted-foreground">Carbohidratos:</span>
                <p className="text-lg font-bold">{results.targetCarbs} g</p>
              </div>
              
              <div>
                <span className="font-medium text-muted-foreground">Grasas:</span>
                <p className="text-lg font-bold">{results.targetFat} g</p>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <span className="font-medium text-muted-foreground">Hierro:</span>
                <p className="text-base">{results.targetIron} mg</p>
              </div>
              
              <div>
                <span className="font-medium text-muted-foreground">Calcio:</span>
                <p className="text-base">{results.targetCalcium} mg</p>
              </div>
              
              <div>
                <span className="font-medium text-muted-foreground">Zinc:</span>
                <p className="text-base">{results.targetZinc} mg</p>
              </div>
              
              <div>
                <span className="font-medium text-muted-foreground">Magnesio:</span>
                <p className="text-base">{results.targetMagnesium} mg</p>
              </div>
              
              <div>
                <span className="font-medium text-muted-foreground">Omega 3:</span>
                <p className="text-base">{results.targetOmega3} g</p>
              </div>
            </div>
          </div>
          
          <Button 
            className={`mt-6 w-full ${isSaved ? 'bg-green-500 hover:bg-green-600' : ''}`}
            onClick={handleSave}
            disabled={isProcessing}
          >
            {isProcessing ? 'Guardando...' : isSaved ? 'Cambios guardados' : 'Guardar Perfil Nutricional'}
          </Button>
        </div>
      )}
    </div>
  );
};
